import Button from '../common/button';

interface Addon {
  id: string;
  name: string;
  description: string;
  icon: 'clock' | 'zap' | 'storage' | 'code';
  amount: number;
  unit: string;
  price: number;
  popular?: boolean;
  currency?: string;
  bonusLimitPercent?: number;
}

interface AddonsProps {
  addons: Addon[];
  onPurchase: (addonId: string) => void;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 }).format(value);

export function Addons({ addons, onPurchase }: AddonsProps) {
  const iconMap = {
    clock: ClockIcon,
    zap: BoltIcon,
    storage: StorageIcon,
    code: CodeIcon,
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-comfortaa mb-3">Дополнения</h1>
        <p className="text-grey1 text-lg">
          Расширяйте тариф дополнительными ресурсами, когда это нужно.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {addons.map((addon) => {
          const Icon = iconMap[addon.icon];

          return (
            <div
              key={addon.id}
              className={`relative rounded-[20px] border bg-white p-6 shadow-[0px_10px_30px_rgba(0,0,0,0.05)] ${
                addon.popular ? 'border-redBase' : 'border-[#EFEFEF]'
              }`}
            >
              {addon.popular && (
                <span className="absolute -top-3 left-4 px-3 py-1 text-xs font-semibold rounded-full bg-redBase text-white">
                  Популярное
                </span>
              )}

              <div className="flex items-start gap-4 mb-4">
                <div className="p-3 rounded-[14px] bg-[#F5F6FA] text-redBase border border-[#EFEFEF]">
                  <Icon />
                </div>
                <div className="flex-1">
                  <h3 className="font-comfortaa mb-2">{addon.name}</h3>
                  <p className="text-sm text-grey1">{addon.description}</p>
                </div>
              </div>

              <div className="mb-5">
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-3xl font-semibold text-blackBase">{formatCurrency(addon.price)}</span>
                  <span className="text-grey2">разово</span>
                </div>
                <p className="text-sm text-grey1">
                  +{addon.amount.toLocaleString('ru-RU')} {addon.unit}
                </p>
              </div>

              <Button
                className="!w-full !text-sm"
                type={addon.popular ? 'black' : 'bordered'}
                onClick={() => onPurchase(addon.id)}
              >
                Купить дополнение
              </Button>
            </div>
          );
        })}
      </div>

      <div className="rounded-[20px] border border-[#EFEFEF] bg-grey5 p-6">
        <h3 className="font-comfortaa mb-4">Как работают дополнения</h3>
        <div className="space-y-4">
          <div className="flex gap-3">
            <StepBadge number={1} />
            <div>
              <h4 className="mb-1">Разовая покупка</h4>
              <p className="text-sm text-grey1">Дополнение покупается один раз и сразу доступно в аккаунте.</p>
            </div>
          </div>
          <div className="flex gap-3">
            <StepBadge number={2} />
            <div>
              <h4 className="mb-1">Срок действия</h4>
              <p className="text-sm text-grey1">
                Самостоятельные пакеты действуют в течение указанного срока; остальные зависят от периода подписки.
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <StepBadge number={3} />
            <div>
              <h4 className="mb-1">Можно комбинировать</h4>
              <p className="text-sm text-grey1">Покупайте несколько дополнений одного типа для увеличения лимита.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const StepBadge = ({ number }: { number: number }) => (
  <div className="flex-shrink-0 mt-1">
    <div className="size-7 rounded-full bg-redBase text-white flex items-center justify-center text-xs font-semibold">
      {number}
    </div>
  </div>
);

const ClockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" className="size-6" aria-hidden="true">
    <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.5" />
    <path d="M12 8v4l2.5 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const BoltIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" className="size-6" aria-hidden="true">
    <path
      d="M13 2L5 13h6l-1 9 8-12h-6l1-8z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
  </svg>
);

const StorageIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" className="size-6" aria-hidden="true">
    <rect x="4" y="6" width="16" height="12" rx="2" stroke="currentColor" strokeWidth="1.5" />
    <path d="M8 10h8M8 14h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const CodeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" className="size-6" aria-hidden="true">
    <path d="M9 8l-4 4 4 4M15 8l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);
